import ContentLoadingState from "@/components/ContentLoadingState";
import { useEffect } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "@/lib/router-compat";
import { useQuery } from "@tanstack/react-query";
import PageHead, { JsonLd } from "@/components/PageHead";
import CountdownTimer from "@/components/webinar/CountdownTimer";
import { ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";
import BlogSEO from "@/components/blog/BlogSEO";

import SplitBanner from "@/components/ui/SplitBanner";
import RelatedContent from "@/components/content/RelatedContent";
import CdxWebinarRelatedContent from "@/components/webinar/CdxWebinarRelatedContent";
import EuMedtechRelatedContent from "@/components/webinar/EuMedtechRelatedContent";
import WebinarHero from "@/components/webinar/WebinarHero";
import WebinarContent from "@/components/webinar/WebinarContent";
import WebinarAudience from "@/components/webinar/WebinarAudience";
import WebinarPartnerAbout from "@/components/webinar/WebinarPartnerAbout";
import WebinarSpeakers from "@/components/webinar/WebinarSpeakers";
import WebinarRegistrationForm from "@/components/webinar/WebinarRegistrationForm";

import WebinarVideoPlayer from "@/components/webinar/WebinarVideoPlayer";
import WebinarChapters from "@/components/webinar/WebinarChapters";
import MedicalInfoCTABanner from "@/components/webinar/MedicalInfoCTABanner";
import type { Chapter } from "@/components/webinar/WebinarChapters";
import TwoWebinarBlock from "@/components/webinar/TwoWebinarBlock";
import { supabase } from "@/integrations/supabase/client";
import { parseWebinarContent } from "@/lib/parseWebinarContent";
import { fetchWebinarForRoute } from "@/lib/webinarLookup";

import webinarPartnersBanner from "@/assets/webinar-partners-banner.webp";
import gendxLogo from "@/assets/partners/gendx-logo.svg";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLanguage, langPrefix } from "@/hooks/useLanguage";
import { uiT } from "@/i18n/ui";
import { localizeHref } from "@/lib/localizeHref";

const SITE_URL = "https://qbdgroup.com";

const WebinarPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get("preview") === "1";
  const isMobile = useIsMobile();
  const lang = useLanguage();
  const t = uiT(lang);
  const navigate = useNavigate();

  const isSpanish = lang === "es";
  const isChinese = lang === "zh-cn";

  const stripMarkdownBold = (text: string | null | undefined): string | undefined =>
    text ? text.replace(/\*\*/g, "").trim() : undefined;

  const { data: post, isLoading } = useQuery({
    queryKey: ["webinar-post", slug, isSpanish, isChinese, isPreview],
    queryFn: () =>
      fetchWebinarForRoute(supabase, slug!, { isSpanish, isChinese, isPreview }),
    enabled: !!slug,
  });


  // If this webinar has a parent_slug, fetch the parent to inherit content
  const parentSlug = (post as any)?.parent_slug as string | null;
  const { data: parentPost } = useQuery({
    queryKey: ["webinar-parent", parentSlug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("webinars")
        .select("*")
        .eq("slug", parentSlug!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!parentSlug,
  });

  // For registration pages of past events, fetch child on-demand entry to get recording_url
  const isRegistrationPage = !post?.parent_slug;
  const isPastRegistration = isRegistrationPage && post?.webinar_date && new Date(post.webinar_date).getTime() <= Date.now();
  const { data: childOnDemand } = useQuery({
    queryKey: ["webinar-child-ondemand", post?.slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("webinars")
        .select("recording_url, cta_label, cta_url, chapters")
        .eq("parent_slug", post!.slug)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!isPastRegistration && !!post?.slug,
  });

  // Merge: child fields take priority, fall back to parent
  // When Spanish, prefer _es columns with English fallback
  const effectiveContent = isSpanish
    ? ((post as any)?.content_es || (parentPost as any)?.content_es || post?.content || parentPost?.content || null)
    : isChinese
      ? ((post as any)?.content_zh || (parentPost as any)?.content_zh || post?.content || parentPost?.content || null)
      : (post?.content || parentPost?.content || null);
  const effectiveSpeakers = post?.speakers?.length ? post.speakers : parentPost?.speakers || null;
  const effectiveTags = post?.tags?.length ? post.tags : parentPost?.tags || null;
  const effectiveCategory = post?.category || parentPost?.category || null;
  const effectiveFeaturedImage = post?.featured_image || parentPost?.featured_image || null;
  const effectiveSummary = isSpanish
    ? ((post as any)?.summary_es || (parentPost as any)?.summary_es || post?.summary || parentPost?.summary || null)
    : isChinese
      ? ((post as any)?.summary_zh || (parentPost as any)?.summary_zh || post?.summary || parentPost?.summary || null)
      : (post?.summary || parentPost?.summary || null);
  const isKalmsWebinar =
    post?.slug === "ce-mark-wont-pay-your-bills-reimbursement-will" ||
    post?.slug_es === "el-marcado-ce-no-paga-las-facturas-el-reembolso-si";
  const rawTitle = isSpanish
    ? ((post as any)?.title_es || post?.title)
    : isChinese
      ? ((post as any)?.title_zh || post?.title)
      : post?.title;
  // Split "Main Title: Subtitle" into separate parts for the detail page.
  // Per-article flag: `<!-- keep_full_title -->` keeps the whole title in the heading,
  // exactly as the overview cards show it.
  const keepFullTitle = /<!--\s*keep_full_title\s*-->/.test(effectiveContent ?? "");
  // Per-article flag: `<!-- hide_hero_subtitle -->` drops the italic subtitle line from the header.
  const hideHeroSubtitle = /<!--\s*hide_hero_subtitle\s*-->/.test(effectiveContent ?? "");
  const colonIdx = keepFullTitle ? -1 : (rawTitle?.indexOf(':') ?? -1);
  const effectiveTitle = colonIdx > 0 ? rawTitle!.slice(0, colonIdx).trim() : rawTitle;
  const titleSubtitle = colonIdx > 0 ? rawTitle!.slice(colonIdx + 1).trim() : undefined;
  const effectiveDuration = post?.duration_minutes ?? parentPost?.duration_minutes ?? null;
  const postCtaLabel = (post as any)?.cta_label;
  const postCtaUrl = (post as any)?.cta_url;
  const ctaExplicitlyDisabled = postCtaUrl === "";
  const effectiveCtaLabel = ctaExplicitlyDisabled
    ? null
    : postCtaLabel || (parentPost as any)?.cta_label || (childOnDemand as any)?.cta_label || null;
  const effectiveCtaUrl = (() => {
    if (ctaExplicitlyDisabled) return null;
    const raw = postCtaUrl || (parentPost as any)?.cta_url || (childOnDemand as any)?.cta_url || null;
    return raw ? localizeHref(raw, lang) : null;
  })();

  const parsed = effectiveContent ? parseWebinarContent(effectiveContent) : null;
  // Per-article flag: `<!-- partner_after_audience -->` moves "About the partner" below "Who should attend"
  const partnerAfterAudience = /<!--\s*partner_after_audience\s*-->/.test(effectiveContent ?? "");

  // Fetch CTA banner data from cta_banners table based on cta_url
  const { data: ctaBanner } = useQuery({
    queryKey: ["cta-banner", effectiveCtaUrl],
    enabled: !!effectiveCtaUrl,
    queryFn: async () => {
      // Try matching by entity_href (strip language prefix for matching)
      const rawUrl = (post as any)?.cta_url || (parentPost as any)?.cta_url || (childOnDemand as any)?.cta_url || null;
      if (!rawUrl) return null;
      const { data, error } = await supabase
        .from("cta_banners")
        .select("title, title_es, title_zh, description, description_es, description_zh, image_url, image_alt, cta_label, cta_label_es, cta_label_zh, entity_href")
        .eq("entity_href", rawUrl)
        .order("sort_order", { ascending: true })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const { data: authors } = useQuery({
    queryKey: ["authors-all"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("authors")
        .select("name, slug, avatar_url, role, bio, bio_es");
      if (error) throw error;
      return data;
    },
  });

  const speakerIntroMatch = effectiveContent?.match(
    /##\s+(?:Speakers?|Speaker Panel|Ponentes?|演讲嘉宾|主讲(?:人|嘉宾)?|讲者)\s*\n\n([^!\n#].+?)(?:\n\n|\n(?=!))/s
  );
  const speakerIntro = speakerIntroMatch
    ? speakerIntroMatch[1].trim().replace(/\*\*/g, "").replace(/\*/g, "")
    : undefined;

  const isFuture = post?.webinar_date
    ? new Date(post.webinar_date).getTime() > Date.now()
    : false;

  // On-demand: only child pages (parent_slug set) — registration pages always stay registration pages
  const isOnDemand = !!post?.parent_slug;

  // Registration is open once a form or link exists, so "submissions open soon" would be wrong
  const hasRegistration = !!(post?.registration_url || post?.hubspot_form_id || post?.webinargeek_id);

  // Effective recording URL: from post, child on-demand, or parent
  const effectiveRecordingUrl = post?.recording_url || (childOnDemand as any)?.recording_url || null;

  // Per-article override: this webinar's recording is already published, so the
  // side panel invites viewing rather than registering.
  const isWatchOnDemandForm =
    post?.slug === "wellness-app-vs-medical-device" ||
    post?.slug_es === "wellness-app-vs-medical-device";
  const formHeading = isWatchOnDemandForm
    ? isChinese
      ? "观看回放"
      : isSpanish
        ? "Ver bajo demanda"
        : "Watch on demand"
    : isChinese
      ? "在此报名"
      : isSpanish
        ? "Regístrate aquí"
        : "Register here";

  // Parse chapters from jsonb column (check child on-demand too)
  const chapters: Chapter[] = (() => {
    const chaptersSource = post?.chapters || (childOnDemand as any)?.chapters;
    if (!chaptersSource) return [];
    try {
      const raw = typeof chaptersSource === "string" ? JSON.parse(chaptersSource) : chaptersSource;
      if (Array.isArray(raw)) return raw as Chapter[];
    } catch {}
    return [];
  })();

  // Redirect EN slug → ZH slug on Chinese routes for canonical URLs
  useEffect(() => {
    if (isChinese && post && (post as any).slug_zh && (post as any).slug_zh !== slug) {
      navigate(`${langPrefix(lang)}/webinars/${(post as any).slug_zh}`, { replace: true });
    }
  }, [isChinese, post, slug, navigate, lang]);

  return (
    <div className="min-h-screen">
      <ScrollToTop />
      <Navbar />

      {isLoading ? (
        <ContentLoadingState />
      ) : !post ? (
        <div className="pt-36 pb-20 text-center">
          <h1 className="text-2xl font-bold text-foreground">
            {t("webinarNotFound")}
          </h1>
          <Link to={localizeHref("/en/knowledge-center?type=webinars", lang)} className="mt-4 inline-flex items-center gap-1 text-accent font-semibold">
            ← {t("backToWebinars")}
          </Link>
        </div>
      ) : (
        <>
          <BlogSEO
            type="post"
            title={effectiveTitle ?? post.title}
            description={stripMarkdownBold(effectiveSummary)}
            slug={isSpanish ? (post.slug_es || post.slug) : isChinese ? ((post as any).slug_zh || post.slug) : post.slug}
            image={effectiveFeaturedImage}
            publishedAt={post.published_at}
            category={effectiveCategory}
            tags={effectiveTags}
            basePath={`${langPrefix(lang)}/webinars`}
            breadcrumbLabel={isChinese ? "网络研讨会" : isSpanish ? "Webinars" : "Webinars"}
          />

          {/* Prevent on-demand pages from being indexed — they are accessed via registration redirect only */}
          {(isOnDemand || ((effectiveTags ?? []) as string[]).some((t: string) => t?.toLowerCase() === "_hidden")) && (
            <PageHead meta={[{ name: "robots", content: "noindex, nofollow" }]} />
          )}

          {/* Event JSON-LD */}
          <JsonLd
            data={{
                "@context": "https://schema.org",
                "@type": "Event",
                name: post.title,
                description: stripMarkdownBold(effectiveSummary) || undefined,
                ...(post.webinar_date ? { startDate: post.webinar_date } : {}),
                ...(effectiveDuration && post.webinar_date
                  ? {
                      endDate: new Date(
                        new Date(post.webinar_date).getTime() + effectiveDuration * 60000
                      ).toISOString(),
                    }
                  : {}),
                eventAttendanceMode: "https://schema.org/OnlineEventAttendanceMode",
                eventStatus: isFuture
                  ? "https://schema.org/EventScheduled"
                  : "https://schema.org/EventMovedOnline",
                location: {
                  "@type": "VirtualLocation",
                  url: post.registration_url || `${SITE_URL}${langPrefix(lang)}/webinars/${isChinese ? ((post as any).slug_zh || post.slug) : isSpanish ? (post.slug_es || post.slug) : post.slug}`,
                },
                image: effectiveFeaturedImage || undefined,
                organizer: {
                  "@type": "Organization",
                  name: "QbD Group",
                  url: SITE_URL,
                },
                ...(effectiveRecordingUrl
                  ? {
                      recordedIn: {
                        "@type": "VideoObject",
                        name: post.title,
                        contentUrl: effectiveRecordingUrl,
                      },
                    }
                  : {}),
                ...(post.registration_url && isFuture
                  ? {
                      offers: {
                        "@type": "Offer",
                        price: "0",
                        priceCurrency: "EUR",
                        url: post.registration_url,
                        availability: "https://schema.org/InStock",
                      },
                    }
                  : {}),
            }}
          />


          <WebinarHero
            title={effectiveTitle ?? post.title}
            subtitle={hideHeroSubtitle ? undefined : (parsed?.subtitle || titleSubtitle)}
            summary={effectiveSummary}
            partnerLogo={isKalmsWebinar
              ? "/__l5e/assets-v1/4d326e6c-3d59-46f8-aeba-1672d105e541/kalms-consulting-logo.png"
              : undefined}
            partnerName={isKalmsWebinar ? "KALMS Consulting" : undefined}
            partnerUrl={isKalmsWebinar ? "https://www.kalmsconsulting.com/" : undefined}
            featuredImage={effectiveFeaturedImage}
            webinarDate={post.webinar_date}
            recordingUrl={effectiveRecordingUrl}
            registrationUrl={post.registration_url}
            durationMinutes={effectiveDuration}
            category={effectiveCategory}
            serviceCategory={post.service_category}
            tags={effectiveTags}
            meta={parsed?.meta}
            isOnDemand={isOnDemand}
          />

          {/* Special two-webinar layout for the PMS & PMCF on-demand page */}
          {isOnDemand && post?.slug === "webinar-on-demand-pms-pmcf-compliance-for-medical-devices-under-eu-mdr" ? (
            <>
              <TwoWebinarBlock />

              {/* CTA banner */}
              {effectiveCtaLabel && effectiveCtaUrl && ctaBanner && (() => {
                const ctaImage = ctaBanner.image_url || effectiveFeaturedImage || post.featured_image || null;
                const ctaDescription = (isChinese ? (ctaBanner as any).description_zh : null) || (isSpanish ? ctaBanner.description_es : null) || ctaBanner.description || '';
                const ctaTitle = (isChinese ? (ctaBanner as any).title_zh : null) || (isSpanish ? ctaBanner.title_es : null) || ctaBanner.title || (isChinese ? "了解我们如何为您提供帮助" : isSpanish ? "Descubre cómo podemos ayudarte" : "Discover how we can help");
                const ctaLbl = (isChinese ? (ctaBanner as any).cta_label_zh : null) || (isSpanish ? ctaBanner.cta_label_es : null) || ctaBanner.cta_label || effectiveCtaLabel;
                if (!ctaImage) return null;
                return (
                  <SplitBanner
                    title={ctaTitle}
                    description={ctaDescription.replace(/\*\*/g, '')}
                    image={ctaImage}
                    imageAlt={ctaBanner.image_alt || effectiveCtaLabel}
                    ctaLabel={ctaLbl}
                    onCtaClick={() => { window.location.href = effectiveCtaUrl; }}
                    containerMaxWidth="max-w-4xl"
                  />
                );
              })()}

              <div className="container mx-auto px-6 lg:px-10 max-w-4xl">
                <hr className="border-border/60" />
              </div>
            </>
          ) : isOnDemand && effectiveRecordingUrl && (
            <>
              <WebinarVideoPlayer recordingUrl={effectiveRecordingUrl} description={effectiveSummary || undefined} />
              <WebinarChapters chapters={chapters} />

              {/* Full-width Medical Information CTA banner (The Future of Medical Information on-demand) */}
              {post?.slug === "the-future-of-medical-information-on-demand" && (
                <MedicalInfoCTABanner
                  title="Build a Medical Information Function That Scales"
                  description="Compliant, multilingual medical information services, fully integrated with pharmacovigilance and quality."
                  ctaLabel="Read more"
                  href="/en/services/pharmacovigilance/comprehensive-medical-information-services"
                />
              )}

              {/* CTA banner right after chapters */}
              {effectiveCtaLabel && effectiveCtaUrl && ctaBanner && (() => {
                const ctaImage = ctaBanner.image_url || effectiveFeaturedImage || post.featured_image || null;
                const ctaDescription = (isChinese ? (ctaBanner as any).description_zh : null) || (isSpanish ? ctaBanner.description_es : null) || ctaBanner.description || '';
                const ctaTitle = (isChinese ? (ctaBanner as any).title_zh : null) || (isSpanish ? ctaBanner.title_es : null) || ctaBanner.title || (isChinese ? "了解我们如何为您提供帮助" : isSpanish ? "Descubre cómo podemos ayudarte" : "Discover how we can help");
                const ctaLbl = (isChinese ? (ctaBanner as any).cta_label_zh : null) || (isSpanish ? ctaBanner.cta_label_es : null) || ctaBanner.cta_label || effectiveCtaLabel;
                if (!ctaImage) return null;
                return (
                  <SplitBanner
                    title={ctaTitle}
                    description={ctaDescription.replace(/\*\*/g, '')}
                    image={ctaImage}
                    imageAlt={ctaBanner.image_alt || effectiveCtaLabel}
                    ctaLabel={ctaLbl}
                    onCtaClick={() => { window.location.href = effectiveCtaUrl; }}
                    containerMaxWidth="max-w-4xl"
                  />
                );
              })()}

              <div className="container mx-auto px-6 lg:px-10 max-w-4xl">
                <hr className="border-border/60" />
              </div>
            </>
          )}


          {/* Content section */}
          <section className="py-16 sm:py-24 bg-background">
            <div className="container mx-auto px-6 lg:px-10 max-w-6xl">
              <div className={`flex flex-col ${isOnDemand ? "" : "md:flex-row"} items-start gap-12 md:gap-16`}>
                {/* Left column: content */}
                <div className={`${isOnDemand ? "max-w-4xl mx-auto w-full" : "flex-1 min-w-0"}`}>
                  {/* Partner logos — only for specific webinars */}
                  {slug === "from-rd-to-market-ip-and-regulatory-strategy" && (
                  <div className="mb-10">
                    <p className="text-xs font-bold uppercase tracking-wider text-accent mb-4">In collaboration with</p>
                    <img
                      src={webinarPartnersBanner}
                      alt="Biovia and Gevers"
                      className="max-w-xs w-full"
                      loading="lazy"
                    />
                  </div>
                  )}
                  {slug === "navigating-grey-zones-growing-pains-regulatory-cdx-space" && (
                  <div className="mb-10 flex items-center gap-4">
                    <p className="text-xs font-bold uppercase tracking-wider text-accent">Co-hosted with</p>
                    <a href="https://www.gendx.com" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
                      <img
                        src={gendxLogo}
                        alt="GenDx"
                        className="h-8 w-auto"
                        loading="lazy"
                      />
                    </a>
                  </div>
                  )}

                  {parsed && (() => {
                    // Deduplicate: if problemFraming starts with the summary text, remove it
                    let pf = parsed.problemFraming;
                    if (pf && effectiveSummary) {
                      const norm = (s: string) => s.replace(/\*\*/g, "").replace(/\s+/g, " ").trim();
                      if (norm(pf).startsWith(norm(effectiveSummary))) {
                        // Find the summary's last ~30 chars in the raw problemFraming to locate the cut point
                        const tail = norm(effectiveSummary).slice(-40);
                        const idx = norm(pf).indexOf(tail);
                        if (idx > -1) {
                          // Map back to raw: find the same tail in the raw string
                          const rawTail = effectiveSummary.replace(/\*\*/g, "").trim().slice(-40);
                          const rawIdx = pf.replace(/\*\*/g, "").indexOf(rawTail);
                          if (rawIdx > -1) {
                            pf = pf.slice(rawIdx + rawTail.length).trim();
                          }
                        }
                      }
                    }
                    return (
                      <WebinarContent
                        problemFraming={pf}
                        whatYoullLearn={parsed.whatYoullLearn}
                        keyTakeaways={parsed.keyTakeaways}
                        agenda={parsed.agenda}
                        aboutTheHosts={parsed.aboutTheHosts}
                        ourSupport={parsed.ourSupport}
                        aboutPartner={parsed.aboutPartner}
                        partnerTitle={parsed.partnerTitle}
                        genericBody={parsed.genericBody}
                        partnerBelowAudience={partnerAfterAudience}
                      />
                    );
                  })()}

                </div>


                {/* Right column: sticky registration form (desktop only, not on-demand) */}
                {!isOnDemand && !isMobile && (post.registration_url || post.hubspot_form_id || post.webinargeek_id || isFuture) && (
                  <div className="w-full md:w-[380px] lg:w-[400px] shrink-0">
                    <div className="space-y-6 md:sticky md:top-28">
                      {isFuture && post.webinar_date && (
                        <div className="rounded-2xl bg-primary p-5">
                          <p className="text-xs uppercase tracking-wider text-primary-foreground/60 mb-3 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
                            {isChinese ? "距离开始" : isSpanish ? "Comienza en" : "Starts in"}
                          </p>
                          <CountdownTimer targetDate={post.webinar_date} />
                          {!hasRegistration && (
                            <p className="text-sm text-primary-foreground/70 mt-3">
                              {isChinese ? "报名即将开放" : isSpanish ? "Las inscripciones se abrirán pronto" : "Submissions open soon"}
                            </p>
                          )}
                        </div>
                      )}
                      {(post.registration_url || post.hubspot_form_id || post.webinargeek_id) && (
                        <WebinarRegistrationForm
                          registrationUrl={post.registration_url}
                          title={effectiveTitle ?? post.title}
                          hubspotFormId={post.hubspot_form_id}
                          webinargeekId={post.webinargeek_id}
                          heading={formHeading}
                          subtitle={
                            (post.slug === "eu-gmp-readiness-supply-chain-strategies-chinese-pharma")
                              ? (isChinese
                                  ? "本场点播 webinar 的报名即将开放。请稍后再来预约观看。"
                                  : "Registration for this on-demand webinar will open soon. Check back shortly to reserve your access.")
                              : isChinese ? "加入 200+ 生命科学行业领袖" : isSpanish ? "Únete a más de 200 líderes en Life Sciences" : "Join 200+ life sciences leaders"
                          }
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Speakers span the full container width so three fit side by side */}
              {parsed && parsed.speakers.length > 0 && (
                <div className={isOnDemand ? "max-w-4xl mx-auto w-full" : ""}>
                  <WebinarSpeakers
                    speakers={parsed.speakers}
                    intro={speakerIntro}
                    authors={authors ?? []}
                  />
                </div>
              )}
            </div>
          </section>


          {/* Mobile: inline registration form (not on-demand) */}
          {!isOnDemand && isMobile && (post.registration_url || post.hubspot_form_id || post.webinargeek_id || isFuture) && (
            <section className="py-12 bg-secondary">
              <div className="container mx-auto px-6 max-w-lg space-y-6">
                {isFuture && post.webinar_date && (
                  <div className="rounded-2xl bg-primary p-5">
                    <p className="text-xs uppercase tracking-wider text-primary-foreground/60 mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
                      {isChinese ? "距离开始" : isSpanish ? "Comienza en" : "Starts in"}
                    </p>
                    <CountdownTimer targetDate={post.webinar_date} />
                    {!hasRegistration && (
                      <p className="text-sm text-primary-foreground/70 mt-3">
                        {isChinese ? "报名即将开放" : isSpanish ? "Las inscripciones se abrirán pronto" : "Submissions open soon"}
                      </p>
                    )}
                  </div>
                )}
                {(post.registration_url || post.hubspot_form_id || post.webinargeek_id) && (
                  <WebinarRegistrationForm
                    registrationUrl={post.registration_url}
                    title={effectiveTitle ?? post.title}
                    hubspotFormId={post.hubspot_form_id}
                    webinargeekId={post.webinargeek_id}
                    heading={formHeading}
                    subtitle={
                      (post.slug === "eu-gmp-readiness-supply-chain-strategies-chinese-pharma")
                        ? (isChinese
                            ? "本场点播 webinar 的报名即将开放。请稍后再来预约观看。"
                            : "Registration for this on-demand webinar will open soon. Check back shortly to reserve your access.")
                        : isChinese ? "加入 200+ 生命科学行业领袖" : isSpanish ? "Únete a más de 200 líderes en Life Sciences" : "Join 200+ life sciences leaders"
                    }
                  />
                )}
              </div>
            </section>
          )}

          {/* Who should attend (hide on on-demand pages) */}
          {!isOnDemand && parsed && (parsed.whoShouldAttend.items.length > 0 || parsed.whoShouldAttend.title) && (
            <WebinarAudience
              title={parsed.whoShouldAttend.title}
              items={parsed.whoShouldAttend.items}
            />
          )}

          {/* "About the partner" opt-in: renders below "Who should attend" */}
          {!isOnDemand && partnerAfterAudience && parsed && parsed.aboutPartner && (
            <section className="py-16 sm:py-20">
              <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
                <WebinarPartnerAbout
                  aboutPartner={parsed.aboutPartner}
                  partnerTitle={parsed.partnerTitle}
                />
              </div>
            </section>
          )}

          {/* More webinars – right after Who should attend */}
          {slug === "navigating-grey-zones-growing-pains-regulatory-cdx-space" ? (
            <CdxWebinarRelatedContent />
          ) : slug === "entering-the-european-medtech-market-why-one-approval-doesnt-fit-all" || slug === "webinar-on-demand-entering-the-european-medtech-market-why-one-approval-doesnt-fit-all" ? (
            <EuMedtechRelatedContent />
          ) : (
            <RelatedContent
              currentId={post.id}
              category={effectiveCategory}
              industryCategory={post.industry_category}
              serviceCategory={post.service_category}
              tags={post.tags}
              contentType="webinars"
              title={isChinese ? "更多 Webinars" : isSpanish ? "Más webinars" : "More webinars"}
              subtitle={isChinese ? "继续观看" : isSpanish ? "Sigue viendo" : "Keep watching"}
            />
          )}

          {/* CTA banner after related content (non-on-demand only, on-demand shows it after chapters) */}
          {!isOnDemand && effectiveCtaLabel && effectiveCtaUrl && ctaBanner && (() => {
            const ctaImage = ctaBanner.image_url || effectiveFeaturedImage || post.featured_image || null;
            const ctaDescription = (isChinese ? (ctaBanner as any).description_zh : null) || (isSpanish ? ctaBanner.description_es : null) || ctaBanner.description || '';
            const ctaTitle = (isChinese ? (ctaBanner as any).title_zh : null) || (isSpanish ? ctaBanner.title_es : null) || ctaBanner.title || (isChinese ? "了解我们如何为您提供帮助" : isSpanish ? "Descubre cómo podemos ayudarte" : "Discover how we can help");
            const ctaLbl = (isChinese ? (ctaBanner as any).cta_label_zh : null) || (isSpanish ? ctaBanner.cta_label_es : null) || ctaBanner.cta_label || effectiveCtaLabel;
            if (!ctaImage) return null;
            return (
              <SplitBanner
                title={ctaTitle}
                description={ctaDescription.replace(/\*\*/g, '')}
                image={ctaImage}
                imageAlt={ctaBanner.image_alt || effectiveCtaLabel}
                ctaLabel={ctaLbl}
                onCtaClick={() => { window.location.href = effectiveCtaUrl; }}
              />
            );
          })()}



          {/* Mobile floating register button (upcoming only) */}
          {!isOnDemand && isMobile && isFuture && post.registration_url && (
            <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-gradient-to-t from-background via-background to-transparent">
              <button
                onClick={() => document.getElementById("register-form")?.scrollIntoView({ behavior: "smooth" })}
                className="w-full py-3.5 rounded-xl bg-accent text-accent-foreground font-semibold text-base shadow-lg hover:bg-accent/90 transition-colors"
              >
                {isChinese ? "立即注册 — 免费" : isSpanish ? "Regístrate — Gratis" : "Register Now — Free"}
              </button>
            </div>
          )}
        </>
      )}

      <Footer />
    </div>
  );
};

export default WebinarPost;
